import { useCallback, useMemo, useState } from 'react'
import { LittleModalCreateEdit } from '../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import { FormElement, FormRender } from '../../../../../../forms/FormRenderer/types'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { dictionaryComplexFromJsonArr } from '../../../../../../utils/globals/dictionary'
import { getSelectedWorkingCenter, getSelectedWorkingCenterStationId, getUserInfo } from '../../../../../../utils/reducers/getters'
import { BeforeSubmitHandler } from '../../../../../../forms/TwinForm/types'
import { SetTPVWorkingCenterInfoTy, TPVWorkingCenterInfoTy } from '../types'
import { changeCurrentWorkingCenter } from '../../../../../../utils/reducers/reduxDispatch'

interface ModalWorkingCenterSelectorProps extends ModalWorkingCenterSelectorLogicProps {
}

const ModalWorkingCenterSelector: React.FC<ModalWorkingCenterSelectorProps> = ({ ...rest }) => {
    const { t } = useTwinTranslation()
    const { selectedInfo, handleChangeWorkingCenter, handleBeforeSubmit, workingCentersMemorized, workingCenterStations } = useModalWorkingCenterSelectorLogic({ ...rest })
   
    let extraFields: FormElement<any>[] = []
    if (workingCenterStations && Object.keys(workingCenterStations).length) {
        extraFields.push({
            name: 'WorkingCenterStationId',
            label: t('workStation', 'Puesto'),
            component: 'CustomSelectWithSearchBar',
            items: workingCenterStations,
            value: selectedInfo.WorkingCenterStationId,
            required: true
        })
    }

    const fields: FormRender<any> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'WorkingCenterId',
                    label: t('workingCenter','Centro de trabajo'),
                    component: 'CustomSelectWithSearchBar',
                    items: workingCentersMemorized,
                    value: selectedInfo.WorkingCenterId,
                    onChange: handleChangeWorkingCenter,
                    required: true
                },
                ...extraFields,
            ]
        },
    ]
    return (
        <LittleModalCreateEdit fields={fields} url={''} translations={{
            title: t('yourWorkingCenter', 'Tu centro de trabajo'),
            button: t('accept', 'Aceptar')
        }} haveButtonPermissions={true} {...rest} onSubmit={rest.onSubmit} beforeSubmit={handleBeforeSubmit} triggerErrors={false} />
    )
}

interface ModalWorkingCenterSelectorLogicProps extends ModalEditComponentProps {
    setWorkingCenterInfo: SetTPVWorkingCenterInfoTy
    workingCenterInfo: TPVWorkingCenterInfoTy
}

interface SelectedInfoProps {
    WorkingCenterId: number
    WorkingCenterStationId?: number
}

const useModalWorkingCenterSelectorLogic = ({ setWorkingCenterInfo, workingCenterInfo }: ModalWorkingCenterSelectorLogicProps) => {
    const user = getUserInfo()
    const MyWorkingCenter = getSelectedWorkingCenter()
    const MyWorkingCenterStationId = workingCenterInfo?.MyWorkingCenterStationId || getSelectedWorkingCenterStationId()
    const [selectedInfo, setSelectedInfo] = useState<SelectedInfoProps>({
        WorkingCenterId: workingCenterInfo?.MyWorkingCenterId || MyWorkingCenter?.id,
        WorkingCenterStationId: MyWorkingCenterStationId
    })
    const workingCentersMemorized = useMemo(() => dictionaryComplexFromJsonArr(user?.WorkingCenters || {}), [user?.WorkingCenters])
    const workingCenterStations = workingCentersMemorized?.[selectedInfo.WorkingCenterId]?.WorkingCenterStations?.length ? dictionaryComplexFromJsonArr(workingCentersMemorized?.[selectedInfo.WorkingCenterId]?.WorkingCenterStations || []) : null

    const handleBeforeSubmit: BeforeSubmitHandler = useCallback((values) => {
        changeCurrentWorkingCenter(values.WorkingCenterId, values.WorkingCenterStationId)
        if (workingCenterStations && !values.WorkingCenterStationId) {
            return false
        }
        setWorkingCenterInfo((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.MyWorkingCenterId = values.WorkingCenterId
            copyOld.MyWorkingCenterStationId = values.WorkingCenterStationId
            copyOld.step = 'checkCashRegister'
            return copyOld
        })
        return true
    }, [setWorkingCenterInfo, workingCenterStations])

    const handleChangeWorkingCenter = useCallback((value: any) => {
        setSelectedInfo((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.WorkingCenterId = value
            copyOld.WorkingCenterStationId = undefined
            return copyOld
        })
    }, [setSelectedInfo])

    return { selectedInfo, handleChangeWorkingCenter, handleBeforeSubmit, workingCentersMemorized, workingCenterStations, MyWorkingCenterStationId }
}

export default ModalWorkingCenterSelector